/* eslint-disable react/no-find-dom-node */

/* eslint-disable no-undef */
import { Icon } from "@iconify/react";
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import Tooltip from "./Tooltip";

export type Action = {
  name: string;
  disabled?: boolean;
  tooltipElement?: string | null;
};

const MAX_MENU_HEIGHT = 100;
const AVG_OPTION_HEIGHT = 40;

export function CTADropdown({
  actions,
  onAction,
  className = "items-end",
  dropdownClassName = "items-end",
  portal = false,
  render = () => (
    <Icon
      icon="carbon:overflow-menu-horizontal"
      className="text-gray-400 placeholderLoader rounded hover:bg-gray-300"
      height={20}
    />
  ),
}: {
  className?: string;
  dropdownClassName?: string;
  actions: Action[];
  onAction: (action: Action) => void;
  // Renders the menu through a portal into document.body, positioned via the
  // trigger's bounding rect. Needed when the trigger lives inside a container
  // (e.g. an ag-grid cell/row) that clips overflowing content, which would
  // otherwise hide the menu for rows near the bottom of the table.
  portal?: boolean;
  render?: () => JSX.Element;
}) {
  const [dropdownOpen, setDropDownOpen] = React.useState(false);
  const contextRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top?: number;
    bottom?: number;
    right: number;
  } | null>(null);

  const updateMenuPosition = () => {
    if (!portal) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuHeight = Math.min(
      MAX_MENU_HEIGHT,
      (actions?.length || 0) * AVG_OPTION_HEIGHT + AVG_OPTION_HEIGHT
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow;
    setMenuPosition({
      top: openUp ? undefined : rect.bottom,
      bottom: openUp ? window.innerHeight - rect.top : undefined,
      right: window.innerWidth - rect.right,
    });
  };

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    updateMenuPosition();
    const handleReposition = () => updateMenuPosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    const checkIfClickOutside = (e: any) => {
      const clickedTrigger = contextRef.current?.contains(e.target);
      const clickedMenu = menuRef.current?.contains(e.target);
      if (!(dropdownOpen && (clickedTrigger || clickedMenu))) {
        setDropDownOpen(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickOutside);
    return () => {
      document.removeEventListener("mousedown", checkIfClickOutside);
    };
  }, [dropdownOpen]);
  const RenderElement = render;

  const menuContent = (
    <div
      id="dropdown"
      ref={menuRef}
      className={
        portal
          ? `z-50 bg-white border divide-y divide-gray-100 rounded shadow fixed ${dropdownClassName}`
          : `absolute z-10 mt-8  bg-white border divide-y divide-gray-100 rounded shadow ${dropdownClassName}`
      }
      style={
        portal && menuPosition
          ? {
            top: menuPosition.top,
            bottom: menuPosition.bottom,
            right: menuPosition.right,
          }
          : undefined
      }
    >
      <p className="px-4 py-1 text-stone-300">More actions</p>
      {actions?.map((action, index) => (
        <Tooltip key={index} text={action.tooltipElement || null} _className="w-56" _position="right-24">
          <a
            key={index}
            className={` block px-4 py-2 hover:bg-slate-50  ${action.disabled
                ? "text-gray-300 cursor-not-allowed "
                : "hover:text-red-500 cursor-pointer"
              } `}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              if (!action.disabled) {
                e.preventDefault();
                onAction(action);
                setDropDownOpen((state) => !state);
              }
            }}
          >
            {action.name}
          </a>
        </Tooltip>
      ))}
    </div>
  );

  return actions ? (
    <div className={`flex flex-col ${className} `} ref={contextRef}>
      <button
        ref={triggerRef}
        className="inline-flex text-xs text-center rounded-lg peer"
        type="button"
        onClick={() => setDropDownOpen((state) => !state)}
      >
        <RenderElement />
      </button>
      {dropdownOpen &&
        (portal
          ? ReactDOM.createPortal(menuContent, document.body)
          : menuContent)}
    </div>
  ) : (
    <div></div>
  );
}
